import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";

// Define the initial state for invoices
const initialState = {
  invoices: [],
  invoiceDetail: null,          
  loadingDetail: false,       
  error: null,
  errorDetail: null,           
};

// Async thunk to get invoices
// Async thunk to get invoices
export const getInvoices = createAsyncThunk(
  "invoices/getInvoices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/invoices");
      const data = response?.data?.data;
      console.log("Fetched Invoices:", data);
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(
        error.response?.data || "Failed to fetch invoices"
      );
    }
  }
);

// Async thunk to add an invoice
export const addInvoice = createAsyncThunk(
  "invoices/addInvoice",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post("/invoices", formData);
      return response.data; // Return the data containing the invoice details
    } catch (error) {
      console.error("API Error:", error); // Log the full error object
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);
export const getInvoiceById = createAsyncThunk(
  "invoices/getInvoiceById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/invoices/${id}`,);
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(
        error.response?.data || "Failed to update invoice"
      );
    }
  }
);

// Async thunk to update an invoice
export const updateInvoice = createAsyncThunk(
  "invoices/updateInvoice",
  async ({ id, invoiceData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/invoices/${id}`, invoiceData);
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(
        error.response?.data || "Failed to update invoice"
      );
    }
  }
);
// redux/invoiceSlice.js   (add this thunk, keep the rest unchanged)

export const getInvoiceDetail = createAsyncThunk(
  "invoices/getInvoiceDetail",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/invoices/${id}`);
      return res?.data?.data;          // <-- adjust to your API shape
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load invoice");
    }
  }
);
// Async thunk to delete an invoice
export const deleteInvoice = createAsyncThunk(
  "invoices/deleteInvoice",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/invoices/${id}/`);
      return id;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(
        error.response?.data || "Failed to delete invoice"
      );
    }
  }
);

// Create the invoice slice
const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
  builder
    // === getInvoices ===
    .addCase(getInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getInvoices.fulfilled, (state, action) => {
      state.loading = false;
      state.invoices = action.payload;
    })
    .addCase(getInvoices.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // === addInvoice ===
    .addCase(addInvoice.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(addInvoice.fulfilled, (state, action) => {
      state.loading = false;
      state.invoices.push(action.payload);
    })
    .addCase(addInvoice.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // === updateInvoice ===
    .addCase(updateInvoice.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateInvoice.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.invoices.findIndex((inv) => inv.id === action.payload.id);
      if (index !== -1) {
        state.invoices[index] = action.payload;
      }
    })
    .addCase(updateInvoice.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // === deleteInvoice ===
    .addCase(deleteInvoice.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteInvoice.fulfilled, (state, action) => {
      state.loading = false;
      state.invoices = state.invoices.filter((inv) => inv.id !== action.payload);
    })
    .addCase(deleteInvoice.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // === getInvoiceDetail === (ONLY ONE SET!)
    .addCase(getInvoiceDetail.pending, (state) => {
      state.loadingDetail = true;
      state.errorDetail = null;
    })
    .addCase(getInvoiceDetail.fulfilled, (state, action) => {
      state.loadingDetail = false;
      state.invoiceDetail = action.payload;
    })
    .addCase(getInvoiceDetail.rejected, (state, action) => {
      state.loadingDetail = false;
      state.errorDetail = action.payload;
    });
},
});

// Export the actions and reducer
export const invoiceReducer = invoiceSlice.reducer;
export const invoiceActions = {
  getInvoices,
  addInvoice,
  updateInvoice,
  deleteInvoice,
  getInvoiceDetail,
};
export const selectInvoiceDetail = (state) => state.invoices.invoiceDetail;
export const selectInvoiceDetailLoading = (state) => state.invoices.loadingDetail;
export const selectInvoiceDetailError = (state) => state.invoices.errorDetail;